import { gql } from "@apollo/client";
import {
  InventorySearchOrderProperty,
  PriceableItemOrigin,
} from "elephant-web/gql";
import {
  type SearchInventoryItemsQueryResult,
  useSearchInventoryItemsLazyQuery,
} from "./__generated__/use-search-new-inventory-lazy-query.gql";
import type {
  SearchNewItemData,
  SearchNewItemHook,
  SearchNewItemHookResult,
} from "./types";

gql`
  query searchInventoryItems(
    $name: String
    $pageSize: Int!
    $pageNumber: Int!
    $facilityId: String!
    $orderBy: InventorySearchOrderProperty!
  ) {
    inventorySearchTmp(
      input: { name: $name, facilityId: $facilityId }
      options: { pageSize: $pageSize, pageNumber: $pageNumber, order: { by: $orderBy } }
    ) {
      totalCount
      edges {
        node {
          sku {
            id
            name
          }
          supplyLevel
          facilityQuantity
        }
      }
    }
  }
`;

function mapGqlResponseToResult({
  data,
  loading,
  error,
}: SearchInventoryItemsQueryResult): SearchNewItemData {
  return {
    loading,
    error,
    data: {
      rows:
        data?.inventorySearchTmp?.edges.map(({ node }) => ({
          id: node.sku.id,
          name: node.sku.name,
          meta: node.supplyLevel
            ? {
                supplyLevel: node.supplyLevel,
                facilityQuantity: node.facilityQuantity || 0,
              }
            : undefined,
          origin: PriceableItemOrigin.Inventory,
        })) ?? [],
      // TODO: Bug in inventorySearchTmp always returns this as the count of ALL SKUs, not only those that would be returned by the filters
      totalRows: data?.inventorySearchTmp.totalCount ?? 0,
    },
  };
}

const useSearchNewInventoryItemLazyQuery: SearchNewItemHook = () => {
  const [query, response] = useSearchInventoryItemsLazyQuery();

  const wrappedQuery: SearchNewItemHookResult[0] = (args) =>
    query({
      variables: {
        ...args.variables,
        // Hack needed because inventorySearchTmp incorrectly ignores pagination params when useing NameRelevance
        // if you don't supply a name, so you get given back _every_ SKU, which is huge.
        orderBy: args.variables.name
          ? InventorySearchOrderProperty.NameRelavance
          : InventorySearchOrderProperty.Name,
      },
    }).then(mapGqlResponseToResult);

  return [wrappedQuery, mapGqlResponseToResult(response)];
};

export default useSearchNewInventoryItemLazyQuery;
